import { NotificationsNoneOutlined } from "@mui/icons-material";
import {
  Box,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import type { Task } from "fhir/r5";

import { NotificationItemCard } from "./NotificationItemCard";

export interface NotificationsPanelBodyProps {
  unreadCount: number;
  loading: boolean;
  accessType?: string | null;
  tasks: Task[];
  updatingById: Record<string, boolean>;
  closeHint?: string;
  onToggleReadStatus: (task: Task) => void;
  onAccessMedicalRecord: (task: Task) => void;
  onRespondQuestionnaire: (task: Task) => void;
}

function NotificationSkeleton() {
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        p: 2,
      }}
    >
      <Skeleton variant="text" width="72%" height={22} />
      <Skeleton variant="text" width="92%" sx={{ mt: 1 }} />
      <Skeleton variant="text" width="48%" sx={{ mt: 0.75 }} />
      <Skeleton
        variant="rounded"
        height={36}
        sx={{ mt: 1.5, borderRadius: 2 }}
      />
    </Box>
  );
}

export function NotificationsPanelBody({
  unreadCount,
  loading,
  accessType,
  tasks,
  updatingById,
  closeHint,
  onToggleReadStatus,
  onAccessMedicalRecord,
  onRespondQuestionnaire,
}: NotificationsPanelBodyProps) {
  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        {unreadCount > 0 ? (
          <Chip
            size="small"
            color="warning"
            label={
              unreadCount === 1 ? "1 não lida" : `${unreadCount} não lidas`
            }
            sx={{ fontWeight: 700, height: 26 }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Tudo em dia
          </Typography>
        )}
        {closeHint ? (
          <Typography variant="caption" color="text.disabled">
            {closeHint}
          </Typography>
        ) : null}
      </Stack>

      {loading ? (
        <Stack spacing={1.25}>
          <NotificationSkeleton />
          <NotificationSkeleton />
        </Stack>
      ) : tasks.length === 0 ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1.25}
          sx={{ py: 4, px: 1, textAlign: "center" }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "action.hover",
            }}
          >
            <NotificationsNoneOutlined
              sx={{ fontSize: 32, color: "text.disabled" }}
            />
          </Box>
          <Typography variant="subtitle2" fontWeight={700}>
            Nenhuma notificação
          </Typography>
          <Typography variant="body2" color="text.secondary" maxWidth={280}>
            Quando houver novidades sobre questionários, prontuário ou tarefas,
            elas aparecerão aqui.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1.25} sx={{ pb: 0.5 }}>
          {tasks.map((task, index) => (
            <NotificationItemCard
              key={task.id ?? `task-${index}`}
              task={task}
              accessType={accessType}
              isUpdating={Boolean(updatingById[task.id ?? ""])}
              onToggleReadStatus={onToggleReadStatus}
              onAccessMedicalRecord={onAccessMedicalRecord}
              onRespondQuestionnaire={onRespondQuestionnaire}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
